import axios from "axios";

/**
 * Get backend base URL based on current hostname
 * For localhost testing with subdomains, automatically detects the hostname
 * and uses the same hostname with port 5100 for the backend
 * This function is called dynamically to ensure it always uses the current hostname
 */
const getBackendBaseUrl = (): string => {
  // If REACT_APP_BACKEND_BASE_URL is explicitly set, use it
  if (process.env.REACT_APP_BACKEND_BASE_URL && process.env.REACT_APP_BACKEND_BASE_URL !== "/") {
    return process.env.REACT_APP_BACKEND_BASE_URL;
  }

  // For localhost subdomain testing, use current hostname with backend port
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // If using subdomain.localhost, use same hostname with backend port
    if (hostname.includes("localhost") || hostname.includes("local")) {
      const backendUrl = `http://${hostname}:5100`;
      return backendUrl;
    }

    // For production/staging, use same hostname with backend port
    const backendUrl = `${window.location.protocol}//${hostname}`;
    return backendUrl;
  }

  // Fallback for SSR or when window is not available
  return "http://localhost:5100";
};

// Create axios instance with dynamic base URL
export const api = axios.create({
  withCredentials: true, // This ensures cookies are sent with requests
});

// Set base URL dynamically on each request to ensure it uses current hostname
api.interceptors.request.use(
  (config) => {
    // Dynamically get backend URL for each request
    const backendBaseUrl = getBackendBaseUrl();
    config.baseURL = `${backendBaseUrl === "/" ? "" : backendBaseUrl}/api`;

    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // For localhost subdomain support, pass the original hostname in a custom header
    // Browsers may normalize store1.localhost to localhost when making requests,
    // so we preserve the original hostname in X-Store-Host header
    if (typeof window !== "undefined") {
      const originalHostname = window.location.hostname;
      const originalPort = window.location.port;
      const fullHost = originalPort ? `${originalHostname}:${originalPort}` : originalHostname;

      // Set X-Store-Host header to preserve the original hostname
      // This helps when browsers normalize localhost subdomains
      config.headers["X-Store-Host"] = fullHost;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle automatic token refresh
api.interceptors.response.use(
  (response) => {
    // Check if server sent a new token (activity-based refresh)
    const newToken = response.headers["x-new-token"];
    if (newToken) {
      // Update token in localStorage and axios defaults
      localStorage.setItem("token", newToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    }
    return response;
  },
  (error) => Promise.reject(error)
);

export const getHealth = async () => {
  return api.get("/health");
};
export const getSystemInfo = async () => {
  return api.get("/info");
};

// Subscription API methods for store admins
export const subscriptionApi = {
  // Get current subscription
  getSubscription: () => api.get("/subscriptions/store"),

  // Get available plans
  getAvailablePlans: () => api.get("/subscriptions/store/plans"),

  // Create subscription
  createSubscription: (data: { planId: string; billingCycle?: "monthly" | "yearly" }) =>
    api.post("/subscriptions/store", data),

  // Update subscription (change plan)
  updateSubscription: (data: { planId: string; prorate?: boolean; pluginIds?: string[] }) =>
    api.put("/subscriptions/store", data),

  // Cancel subscription
  cancelSubscription: (data?: { cancelAtPeriodEnd?: boolean }) =>
    api.delete("/subscriptions/store", { data }),

  // Get business model
  getBusinessModel: () => api.get("/subscriptions/store/business-model"),

  // Switch business model
  switchBusinessModel: (data: { businessModel: "plugin-based" | "pay-as-you-go"; transactionFeePercentage?: number }) =>
    api.put("/subscriptions/store/business-model", data),

  // Get payment intent client secret for incomplete subscription
  generatePaymentLink: () =>
    api.post("/subscriptions/store/payment-link"),

  // Renew subscription manually (for manual renewal subscriptions)
  renewSubscription: (data?: { switchToAutoRenewal?: boolean }) =>
    api.post("/subscriptions/store/renew", data || {}),

  // Refresh subscription status from Stripe
  refreshSubscriptionStatus: () =>
    api.post("/subscriptions/store/refresh"),

  // Create payment intent for manual renewal subscription
  createManualRenewalPayment: () =>
    api.post("/subscriptions/store/manual-renewal-payment"),

  // Cancel scheduled changes
  cancelScheduledChanges: () =>
    api.post("/subscriptions/store/cancel-scheduled"),

  // Subscription request methods
  createSubscriptionRequest: (data: {
    planId: string;
    billingCycle: "monthly" | "yearly";
    requestType: "create" | "renew";
    pluginIds?: string[];
    reason?: string;
  }) => api.post("/subscriptions/store/request", data),

  getStoreRequests: () => api.get("/subscriptions/store/requests"),
};

// Payout API methods for store admins
export const payoutApi = {
  // Get payout history
  getPayoutHistory: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get("/payouts/store", { params }),

  // Get payout summary
  getPayoutSummary: () => api.get("/payouts/store/summary"),

  // Get specific payout
  getPayout: (payoutId: string) => api.get(`/payouts/store/${payoutId}`),

  // Pay negative payout
  payNegativePayout: (payoutId: string) => api.post(`/payouts/store/${payoutId}/pay`),
};

// Plugin API methods for store admins
export const pluginApi = {
  // Get enabled plugins for the store
  getEnabledPlugins: () => api.get("/plugins/store/enabled"),

  // Get available plugins (not yet enabled) for the store
  getAvailablePlugins: () => api.get("/plugins/store/available"),
};

// Stripe configuration API methods for location admins
export const stripeConfigApi = {
  // Get location Stripe configuration (public - only publishable key) by location ID
  getLocationStripeConfig: (locationId: string) => api.get(`/locations/admin/${locationId}/stripe-config`),

  // Get location Stripe configuration (public - only publishable key) by country code (public endpoint)
  getLocationStripeConfigByCountry: () => api.get("/locations/stripe-config"),

  // Save location Stripe configuration (supports partial updates)
  saveLocationStripeConfig: (locationId: string, data: { publishableKey?: string; secretKey?: string; webhookSecret?: string | null; paymentMethodTypes?: string[] }) =>
    api.post(`/locations/admin/${locationId}/stripe-config`, data),

  // Delete location Stripe configuration
  deleteLocationStripeConfig: (locationId: string) => api.delete(`/locations/admin/${locationId}/stripe-config`),
};

export const paypalConfigApi = {
  // Get location PayPal configuration (public - only client ID) by location ID
  getLocationPayPalConfig: (locationId: string) => api.get(`/locations/admin/${locationId}/paypal-config`),

  // Get location PayPal configuration (public - only client ID) by country code (public endpoint)
  getLocationPayPalConfigByCountry: () => api.get("/locations/paypal-config"),

  // Save location PayPal configuration
  saveLocationPayPalConfig: (locationId: string, data: { clientId: string; clientSecret: string }) =>
    api.post(`/locations/admin/${locationId}/paypal-config`, data),

  // Delete location PayPal configuration
  deleteLocationPayPalConfig: (locationId: string) => api.delete(`/locations/admin/${locationId}/paypal-config`),
};
